/**
 * Get the laboratory logo in the specified format
 * @param format The format of the logo ("svg" or "png")
 * @returns URL for fetching the laboratory logo
 */
export function getLaboratoryLogo(format: "svg" | "png"): string {
  return `/laboratory/logo/${format}`;
}

/**
 * Get the laboratory name based on the culture
 * @param culture Culture identifier
 * @returns URL for fetching laboratory name
 */
export function getLaboratoryName(culture: string): string {
  return `/laboratory/name?culture=${culture}`;
}

/**
 * Get the patient's visits based on pagination and optional filter
 * @param pageNumber Page number
 * @param pageSize Number of items per page
 * @param filter Optional filter string to apply to the visits
 * @returns URL for fetching visits
 */
export function getVisits(
  pageNumber: number,
  pageSize: number,
  filter?: string
): string {
  let url = `/visits?pageNumber=${pageNumber}&pageSize=${pageSize}`;

  // If filter is provided, append it to the URL
  if (filter) {
    url += `&filter=${filter}`;
  }

  return url;
}

/**
 * Get the patient's results based on the visitId
 * @param visitId Visit ID
 * @returns URL for fetching results by visit ID
 */
export function getResultsByVisitId(visitId: number): string {
  return `/visits/${visitId}/results`;
}

/**
 * Get the patient's balance based on the visitId
 * @param visitId Visit ID
 * @returns URL for fetching results by visit ID
 */
export function getBalanceByVisitId(visitId: number): string {
  return `/visits/${visitId}/balance`;
}

/**
 * Get the patient by its ID
 * @param patientId Patient ID
 * @returns URL for fetching the patient by its ID
 */
export function getPatient(patientId: number): string {
  return `/patients/${patientId}`;
}

/**
 * Get the tests for a specific patient by patient ID
 * @param patientId Patient ID
 * @returns URL for fetching tests by patient ID
 */
export function getPatientTests(patientId: number): string {
  return `/patients/${patientId}/tests`;
}

/**
 * Get the test results over time in descending order
 * @param patientId Patient ID
 * @param testId Test ID
 * @returns URL for fetching test results over time
 */
export function getTestResults(patientId: number, testId: number): string {
  return `/patients/${patientId}/tests/${testId}`;
}

/**
 * Get the visits for a specific patient by patient ID
 * @param patientId Patient ID
 * @returns URL for fetching visits by patient ID
 */
export function getPatientVisits(
  patientId: number,
  pageNumber: number,
  pageSize: number
): string {
  return `/patients/${patientId}/visits?pageNumber=${pageNumber}&pageSize=${pageSize}`;
}

/**
 * Get the laboratory terms of use
 * @returns URL for fetching laboratory terms of use
 */
export function getLaboratoryTermsOfUse(): string {
  return `/laboratory/terms-of-use`;
}
